import { db } from '@/firebase/config'
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  Timestamp
} from 'firebase/firestore'

/**
 * Save notification to Firestore for user
 * @param {string} userId - User ID
 * @param {string|Object} recipientRoleOrNotification - Recipient role (if 3 params) or notification object (if 2 params)
 * @param {Object} [notification] - Notification data (if 3 params)
 */
export const saveUserNotification = async (
  userId,
  recipientRoleOrNotification,
  notification
) => {
  try {
    // Handle both 2-parameter (userId, notification) and 3-parameter (userId, recipientRole, notification) calls
    let recipientRole = null
    let notificationData = null

    if (notification !== undefined) {
      // 3-parameter call: (userId, recipientRole, notification)
      recipientRole = recipientRoleOrNotification
      notificationData = notification
    } else {
      // 2-parameter call: (userId, notification)
      notificationData = recipientRoleOrNotification
      // Try to extract role from notification or user data
      recipientRole = notificationData.recipientRole || null
    }

    // Calculate expiry date: 30 days from now
    const now = new Date()
    const expiryDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000) // 30 days in milliseconds
    const expiresAtTimestamp = Timestamp.fromDate(expiryDate)

    // Handle both old format (title/message) and new format (type/body/metadata)
    const finalNotificationData = {
      userId,
      recipientId: userId,
      recipientRole: recipientRole,
      type: notificationData.type || 'info',
      body:
        notificationData.body ||
        notificationData.message ||
        notificationData.title ||
        '',
      metadata: notificationData.metadata || {},
      channel: notificationData.channel || 'in_app',
      hasAction:
        notificationData.hasAction !== undefined
          ? notificationData.hasAction
          : notificationData.action
          ? true
          : false,
      action:
        notificationData.action?.label ||
        (typeof notificationData.action === 'string'
          ? notificationData.action
          : null),
      actionUrl:
        notificationData.action?.url || notificationData.actionUrl || null,
      status: 'unread',
      read: false,
      readAt: null,
      createdAt: serverTimestamp(),
      expiresAt: notificationData.expiresAt || expiresAtTimestamp
    }

    await addDoc(collection(db, 'notifications'), finalNotificationData)

    // Trigger immediate notification check
    window.dispatchEvent(new CustomEvent('checkNotifications'))
  } catch (error) {
    console.error('Error saving notification:', error)
  }
}

/**
 * Get user notifications
 * @param {string} userId - User ID
 * @param {boolean} unreadOnly - Only get unread notifications
 * @returns {Promise<Array>} Array of notifications
 */
export const getUserNotifications = async (userId, unreadOnly = false) => {
  try {
    const notificationsRef = collection(db, 'notifications')
    let q = query(
      notificationsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    )

    if (unreadOnly) {
      q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('read', '==', false),
        orderBy('createdAt', 'desc')
      )
    }

    const querySnapshot = await getDocs(q)
    const notifications = []

    querySnapshot.forEach((doc) => {
      notifications.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return notifications
  } catch (error) {
    console.error('Error getting notifications:', error)
    return []
  }
}

/**
 * Mark notification as read
 * @param {string} notificationId - Notification ID
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId)
    await updateDoc(notificationRef, {
      read: true,
      readAt: serverTimestamp()
    })
  } catch (error) {
    console.error('Error marking notification as read:', error)
  }
}

/**
 * Update notification metadata
 * @param {string} notificationId - Notification ID
 * @param {Object} metadataUpdate - Metadata fields to update
 */
export const updateNotificationMetadata = async (
  notificationId,
  metadataUpdate
) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId)
    const notificationDoc = await getDoc(notificationRef)

    if (!notificationDoc.exists()) {
      throw new Error('Notification not found')
    }

    const currentData = notificationDoc.data()
    const currentMetadata = currentData.metadata || {}

    // Merge existing metadata with updates
    const updatedMetadata = {
      ...currentMetadata,
      ...metadataUpdate
    }

    await updateDoc(notificationRef, {
      metadata: updatedMetadata,
      updatedAt: serverTimestamp()
    })
  } catch (error) {
    console.error('Error updating notification metadata:', error)
    throw error
  }
}

/**
 * Delete notification
 * @param {string} notificationId - Notification ID
 */
export const deleteNotification = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId)
    await deleteDoc(notificationRef)
  } catch (error) {
    console.error('Error deleting notification:', error)
    throw error
  }
}

/**
 * Delete all notifications for a user
 * @param {string} userId - User ID
 */
export const deleteAllNotifications = async (userId) => {
  try {
    const notifications = await getUserNotifications(userId)
    const deletePromises = notifications.map((notification) =>
      deleteNotification(notification.id)
    )
    await Promise.all(deletePromises)
  } catch (error) {
    console.error('Error deleting all notifications:', error)
    throw error
  }
}
